import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import * as amplitude from "@amplitude/analytics-browser";

import type { IButton } from "libs/design-system/src/types/Button/button";
import { Check, Xmark } from "@unified-checkout/design-system/icons/index";
import {
  Button,
  Modal,
  SkeletonBase,
  StateIcon
} from "@unified-checkout/design-system";

import {
  BULK_PAYMENT_LINKS_EVENTS,
  BULK_PAYMENT_LINKS_EVENTS_PARAMS,
  BULK_PAYMENT_LINKS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/payment_links/bulk";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { THREE_SECONDS } from "@app/dashboard/constants/shared";
import { useGoBack } from "@app/dashboard/hooks/useGoBack";
import { useApplyThenNavigate } from "@app/dashboard/hooks/useApplyThenNavigate";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { notify } from '@app/dashboard/utils/notifier';
import {
  submitBulkPaymentLink
} from "@app/dashboard/services/inner_layout/bulk_payment_links/submit-bulk-payment-link";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import type {
  IFooter
} from "@app/dashboard/types/innerLayout/payment_links/bulk/sections/footer";
import {
  MAX_NOTIFICATION_WIDTH,
} from "../../../utils";
import styles from "./footer.module.scss";


export const Footer = ({
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  fullPageStatus,
  setFullPageStatus,
  amountsFrom,
}: IFooter) => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();

  const applyThenNavigate = useApplyThenNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const rowCount = ((formValues as any)?.bulkCustomers?.length - 1) || 0;

  const {
    amountCents,
    useFixedAmount,
    bulkCustomers,
    paymentMethodsIds,
    saveSelection,
    expiresAt
  } = (formValues || {}) as any;

  const mutationFnParams = {
    amountCents,
    useFixedAmount: !!useFixedAmount,
    bulkCustomers,
    paymentMethods: paymentMethodsIds,
    saveSelection: !!saveSelection,
    expiresAt,
    isLive
  };

  const resetBeforeNavigation = () => {
    setIsModalOpen(false);
    setFormValues({});
    setFormErrors({});
  };

  const {
    isPending,
    mutate: mutate,
    isSuccess,
  } = useMutation({
    mutationKey: [QUERY_KEYS.CREATE_BULK_PAYMENT_LINK],
    mutationFn: () => submitBulkPaymentLink(mutationFnParams),
    onSuccess: () => {
      setIsModalOpen(true);

      amplitude.track(BULK_PAYMENT_LINKS_EVENTS.PAYMENT_LINK_CREATED, {
        [BULK_PAYMENT_LINKS_EVENTS_PARAMS.LINKS_CREATED_COUNT]: rowCount
      });

      setTimeout(() => {
        applyThenNavigate(resetBeforeNavigation, INNER_ROUTES.QUICK_LINKS);
      }, THREE_SECONDS);
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message || t('errors.somethingWentWrong');

      amplitude.track(BULK_PAYMENT_LINKS_EVENTS.CREATEION_FAILED, {
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: msg
      });

      notify.error(msg, { maxWidth: MAX_NOTIFICATION_WIDTH, icon: Xmark });
    }
  });

  useEffect(() => {
    setFullPageStatus(prev => ({ ...prev, disabled: isPending }));
  }, [isPending]);

  const isValidFormValues = () => {
    const {
      bulkCustomers,
      paymentMethodsIds,
      useFixedAmount,
      amountCents,
      customExpDate,
      expiresAt
    } = formValues as any;

    const isValid =
      !!bulkCustomers?.length &&
      !!paymentMethodsIds.length &&
      (useFixedAmount ? !!amountCents : true) &&
      (customExpDate ? !!expiresAt : true);

    return isValid;
  };

  const goBack = useGoBack();

  const onSubmitHandler = () => {
    mutate();

    const amountSource = amountsFrom === 'fixed' ?
      BULK_PAYMENT_LINKS_EVENTS_VALUES.FIXED
      :
      BULK_PAYMENT_LINKS_EVENTS_VALUES.UPLOADED_SHEET;

    amplitude.track(BULK_PAYMENT_LINKS_EVENTS.SUBMIT_CLICKED, {
      [BULK_PAYMENT_LINKS_EVENTS_PARAMS.ROW_COUNT]: rowCount,
      [BULK_PAYMENT_LINKS_EVENTS_PARAMS.AMOUNT_SOURCE]: amountSource
    });
  };

  const isCreateBtnDisabled =
    !isValidFormValues() ||
    Object.values(formErrors).some(err => err);

  const actionBtnsProps: IButton[] = [
    {
      size: "xl",
      color: 'secondary',
      variant: 'outlined',
      text: t('form.cancel'),
      onClickHandler: () => goBack()
    },
    {
      size: "xl",
      color: 'primary',
      variant: 'contained',
      text: t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.actions.createAndShare'),
      disabled: isCreateBtnDisabled,
      loading: isPending,
      onClickHandler: () => onSubmitHandler()
    }
  ];

  return (
    <>
      <div className={styles["footer"]}>
        {
          fullPageStatus.loading ?
            <SkeletonBase width="20rem" height="3.375rem" />
            :
            actionBtnsProps.map((btn, index) =>
              <Button
                key={`bulk-pl-footer-action-${index}`}
                {...btn}
              />
            )
        }

        <Modal
          open={isModalOpen}
          closeOnClickOutside={false}
          onCloseHandler={() => setIsModalOpen(false)}
          hasCloseIcon
        >
          <div className={styles["footer__success-modal"]}>

            <StateIcon
              status="success"
              icon={Check}
            />

            <div className={styles["footer__success-modal__text-box"]}>

              <div className={styles["footer__success-modal__text-box__title"]}>
                {t('innerLayout.paymentLinksCreationPages.sections.payment.paymentsAreLive')}
              </div>
              <div className={styles["footer__success-modal__text-box__hint"]}>
                {t('innerLayout.paymentLinksCreationPages.sections.payment.paymentsCreatedAndSent')}
              </div>

            </div>
          </div>

        </Modal>

      </div>
      
    </>
  )
}
